import { useEffect } from 'react';

interface SchemaOrgProps {
  type: 'Organization' | 'WebSite' | 'Service' | 'FAQPage' | 'BreadcrumbList' | 'WebPage';
  data: Record<string, any>;
}

export function useSchemaOrg({ type, data }: SchemaOrgProps) {
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    let schema: Record<string, any> = {
      '@context': 'https://schema.org',
      '@type': type,
    };

    if (type === 'Organization') {
      schema = {
        ...schema,
        name: data.name || 'Yepes WebLab',
        url: siteUrl,
        logo: data.logo || `${siteUrl}/logo.png`,
        description: data.description || 'Custom web development and automation solutions',
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'Customer Service',
          email: data.email || 'contact@example.com',
        },
        sameAs: data.socialLinks || [],
      };
    } else if (type === 'WebSite') {
      schema = {
        ...schema,
        name: data.name || 'Yepes WebLab',
        url: siteUrl,
        description: data.description || 'Transform your bold ideas into reality with custom web development and automation solutions',
        potentialAction: {
          '@type': 'SearchAction',
          target: `${siteUrl}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      };
    } else if (type === 'Service') {
      schema = {
        ...schema,
        serviceType: data.serviceType || 'Web Development',
        provider: {
          '@type': 'Organization',
          name: data.providerName || 'Yepes WebLab',
          url: siteUrl,
        },
        areaServed: data.areaServed || 'Worldwide',
        description: data.description || 'Professional web development and automation services',
      };
    } else if (type === 'WebPage') {
      schema = {
        ...schema,
        name: data.name || 'Yepes WebLab',
        url: data.url || siteUrl,
        description: data.description || 'Professional web development and automation services',
        inLanguage: 'en-US',
        isPartOf: {
          '@type': 'WebSite',
          name: 'Yepes WebLab',
          url: siteUrl,
        },
      };
    } else {
      schema = { ...schema, ...data };
    }

    const scriptId = `schema-${type.toLowerCase()}`;
    let scriptTag = document.getElementById(scriptId);

    if (!scriptTag) {
      scriptTag = document.createElement('script');
      scriptTag.id = scriptId;
      scriptTag.type = 'application/ld+json';
      document.head.appendChild(scriptTag);
    }

    scriptTag.textContent = JSON.stringify(schema);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [type, data]);
}

export function generateServiceSchema(services: Array<{ name: string; description: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return services.map((service) => ({
    '@type': 'Service',
    serviceType: service.name,
    provider: {
      '@type': 'Organization',
      name: 'Yepes WebLab',
      url: siteUrl,
    },
    description: service.description,
    areaServed: 'Worldwide',
  }));
}
